/**
 * Canonical investment strategies (a.k.a. project types).
 * Strategy and project type are the same thing - this is the single list used by the public
 * site and the admin, so a project never reads as two different things.
 * Mirrors backend/constants/project_types.py.
 */
import { Hammer, Buildings, TrendUp, Stack, GraduationCap, House, Tag } from '@phosphor-icons/react';

export const PROJECT_TYPES = [
  {
    value: 'renovate_sell',
    label: 'Renovate & Sell',
    description: 'Buy, renovate and sell',
    icon: Hammer,
    pill: 'bg-amber-500/70 text-white backdrop-blur-sm',
    ink: 'text-amber-600',
    soft: 'bg-amber-500/12 text-amber-700',
    badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  },
  {
    value: 'renovate_rent',
    label: 'Renovate & Rent',
    description: 'Renovate and hold for rental income',
    icon: TrendUp,
    pill: 'bg-emerald-600/70 text-white backdrop-blur-sm',
    ink: 'text-emerald-600',
    soft: 'bg-emerald-500/12 text-emerald-700',
    badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Renovate, rent for a period, then sell',
    icon: Stack,
    pill: 'bg-slate-700/70 text-white backdrop-blur-sm',
    ink: 'text-slate-700',
    soft: 'bg-slate-500/12 text-slate-700',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  },
  {
    value: 'student_housing_rental',
    label: 'Student Housing',
    description: 'Student accommodation let room by room',
    icon: GraduationCap,
    pill: 'bg-blue-600/70 text-white backdrop-blur-sm',
    ink: 'text-blue-600',
    soft: 'bg-blue-500/12 text-blue-700',
    badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  {
    value: 'new_development_sale',
    label: 'New Development',
    description: 'Ground-up construction for sale',
    icon: Buildings,
    pill: 'bg-violet-500/70 text-white backdrop-blur-sm',
    ink: 'text-violet-600',
    soft: 'bg-violet-500/12 text-violet-700',
    badge: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  },
  {
    value: 'residential_rental',
    label: 'Rental',
    description: 'Residential let, no major works',
    icon: House,
    pill: 'bg-teal-600/70 text-white backdrop-blur-sm',
    ink: 'text-teal-600',
    soft: 'bg-teal-500/12 text-teal-700',
    badge: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Anything else',
    icon: Tag,
    pill: 'bg-slate-700/70 text-white backdrop-blur-sm',
    ink: 'text-slate-700',
    soft: 'bg-slate-500/12 text-slate-700',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  },
];

/** Every value ever stored, mapped onto the canonical list. */
export const LEGACY_PROJECT_TYPES = {
  flipping: 'renovate_sell',
  flip: 'renovate_sell',
  hold_yield: 'renovate_rent',
  acquire_hold: 'residential_rental',
  buy_to_rent: 'residential_rental',
  rental: 'residential_rental',
  development: 'new_development_sale',
  new_development: 'new_development_sale',
  develop_sell: 'new_development_sale',
  land_development: 'new_development_sale',
  hybrid_hold_sale: 'hybrid',
  reposition: 'hybrid',
  student_housing: 'student_housing_rental',
};

/** Strategies whose returns come from rent rather than a sale. */
export const RENTAL_PROJECT_TYPES = [
  'renovate_rent',
  'residential_rental',
  'student_housing_rental',
  'hybrid',
];

export const normalizeProjectType = (value) =>
  (value && (LEGACY_PROJECT_TYPES[value] || value)) || 'other';

export const projectTypeMeta = (value, labelOverride) => {
  const canonical = normalizeProjectType(value);
  const meta = PROJECT_TYPES.find((t) => t.value === canonical) || PROJECT_TYPES[PROJECT_TYPES.length - 1];
  return { ...meta, label: labelOverride || meta.label };
};

export const projectTypeLabel = (value) => projectTypeMeta(value).label;

export const isRentalStrategy = (value) => RENTAL_PROJECT_TYPES.includes(normalizeProjectType(value));

export default PROJECT_TYPES;
